import { enumMembers, type ApiSchema } from "../../apiSchema";
import {
  JsonContractError,
  readArray,
  readBoolean,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../../http/jsonDecoding";
import { decodeItemSelector, decodeRecord, oneOf, readStrings, VALUE_KINDS, type ValueKind } from "./selectorDecoders";
import { decodeStepDefinition, type StepDefinition } from "./stepDecoders";
import { WORK_AREAS, WORKFLOW_PURPOSES } from "../workflowsApi";
import { decodeTranslations, keptMembers, PROCESS_MEMBERS } from "./keptContentMembers";
import { decodeInputDeclaration } from "./declarationDecoders";
import { decodeToolParameter } from "./toolParameterDecoders";
export { decodeToolParameter } from "./toolParameterDecoders";

/**
 * The process items the authoring forms start from (processes.md P-1): a step, a
 * Workflow, an artifact or a prompt whole; a tool's declared interface without its code;
 * a skill or a label list whole.
 */

export type WorkflowContent = ApiSchema<"WorkflowContent">;
/** A Workflow input: a step input it fills, and how a run fills it (ADR-0032 D3). */
export type WorkflowInputDeclaration = ApiSchema<"WorkflowInputDeclaration">;
export type ArtifactContent = ApiSchema<"ArtifactContent">;
export type PromptContent = ApiSchema<"PromptContent">;
export type SkillContent = ApiSchema<"SkillContent">;
export type CapabilityOverridesContent = ApiSchema<"CapabilityOverridesContent">;
export type ToolParameter = ApiSchema<"ToolParameter">;

/** What a tool takes and gives (P-3): the step form offers exactly this. */
export interface ToolInterface {
  readonly name: string;
  readonly description: string;
  readonly inputs: Readonly<Record<string, ApiSchema<"ToolInput">>>;
  readonly parameters: Readonly<Record<string, ToolParameter>>;
  readonly outputs: Readonly<Record<string, { readonly kind: ValueKind; readonly description: string | null }>>;
}

export type LabelListContent = ApiSchema<"LabelListContent">;

export type ProcessItemContent =
  | { readonly kind: "step"; readonly definition: StepDefinition }
  | { readonly kind: "workflow"; readonly definition: WorkflowContent }
  | { readonly kind: "artifact"; readonly definition: ArtifactContent }
  | { readonly kind: "prompt"; readonly definition: PromptContent }
  | { readonly kind: "tool"; readonly definition: ToolInterface }
  | { readonly kind: "skill"; readonly definition: SkillContent }
  | { readonly kind: "label_list"; readonly definition: LabelListContent }
  | { readonly kind: "capability_overrides"; readonly definition: CapabilityOverridesContent };

const ROLES = enumMembers<ApiSchema<"WorkflowRole">>({ main: true, input: true });
const STARTS = enumMembers<ApiSchema<"StepStart">>({ automatic: true, on_request: true });
const MEDIA_TYPES = enumMembers<ApiSchema<"ArtifactMediaType">>({ "text/markdown": true, "application/json": true });

/** The `definition` member of an item version, by the item's kind; `null` for other kinds. */
export function decodeProcessItemContent(kind: string, value: unknown): ProcessItemContent | null {
  if (value === null || value === undefined) {
    return null;
  }
  const content = readObject(value, "definition");
  switch (kind) {
    case "step":
      return { kind, definition: decodeStepDefinition(content["definition"]) };
    case "workflow":
      return { kind, definition: decodeWorkflowContent(content) };
    case "artifact":
      return { kind, definition: decodeArtifactContent(content) };
    case "prompt":
      return { kind, definition: { kind, name: readString(content, "name"), text: readString(content, "text"), ...keptMembers(content, { ...PROCESS_MEMBERS, description: (description) => readString({ description }, "description") }) } };
    case "tool":
      return { kind, definition: decodeToolInterface(content) };
    case "skill":
      return { kind, definition: decodeSkillContent(content) };
    case "capability_overrides":
      return { kind, definition: decodeCapabilityOverrides(content) };
    case "label_list":
      return {
        kind,
        definition: {
          kind: "label_list",
          name: readString(content, "name"),
          description: readString(content, "description"),
          ...keptMembers(content, PROCESS_MEMBERS),
          labels: readArray(content["labels"], "labels", (label) => {
            const entry = readObject(label, "label");
            return {
              key: readString(entry, "key"),
              name: readString(entry, "name"),
              description: readString(entry, "description"),
              guidelines: readString(entry, "guidelines"),
              examples: readStrings(entry["examples"], "examples"),
              ontology_terms: readStrings(entry["ontology_terms"], "ontology terms"),
            };
          }),
        },
      };
    default:
      return null;
  }
}

/** A Workflow input: a step input's declaration and the step input it fills (`<flat key>.<input>`). */
export function decodeWorkflowInputDeclaration(value: unknown): WorkflowInputDeclaration {
  const declaration = readObject(value, "Workflow input declaration");
  return { ...decodeInputDeclaration(declaration), destination: readString(declaration, "destination") };
}

/** The members of a Workflow the editor reads and writes itself; every other member is kept as it came. */
type ReadWorkflowMembers = "kind" | "name" | "description" | "structure" | "steps" | "exports" | "derived_from";

/**
 * A reader for every other member of the schema's `WorkflowContent`, optional ones included (`-?`):
 * a member the server adds fails the typecheck here until it is read or kept, so saving can't drop
 * it silently (WD-1+3; W-22 landing 2 lost a declaration that way).
 */
type WorkflowKeptReaders = { readonly [K in Exclude<keyof WorkflowContent, ReadWorkflowMembers>]-?: (value: unknown) => NonNullable<WorkflowContent[K]> };

const WORKFLOW_KEPT_READERS = {
  ...PROCESS_MEMBERS,
  types: (types: unknown) => readStrings(types, "types"),
  declared_inputs: (declared: unknown) => readArray(declared, "declared inputs", decodeWorkflowInputDeclaration),
  purpose: (purpose: unknown) => readOneOf({ purpose }, "purpose", WORKFLOW_PURPOSES),
  theme: (theme: unknown) => readOneOf({ theme }, "theme", WORK_AREAS),
  translations: decodeTranslations,
  outcome_conditions: (conditions: unknown) => readArray(conditions, "outcome conditions", decodeOutcomeCondition),
  proven: (proven: unknown) => readBoolean({ proven }, "proven"),
} satisfies WorkflowKeptReaders;

export function decodeWorkflowContent(content: JsonObject): WorkflowContent {
  const derivedFrom = content["derived_from"];
  // A list of sources and project types (processes.md P-18) stay, so saving the Workflow again keeps them.
  return {
    kind: "workflow",
    name: readString(content, "name"),
    description: readString(content, "description"),
    structure: { kind: "sequence" },
    steps: readArray(content["steps"], "Workflow steps", decodeWorkflowStep),
    exports: decodeRecord(content["exports"], "exports", decodeBinding),
    derived_from: typeof derivedFrom === "string" ? derivedFrom : Array.isArray(derivedFrom) ? readStrings(derivedFrom, "derived from") : null,
    ...keptMembers(content, WORKFLOW_KEPT_READERS),
  };
}

/** What a run must show (W-22), kept as the server sent it so saving keeps it. */
function decodeOutcomeCondition(value: unknown): ApiSchema<"OutcomeCondition"> {
  const condition = readObject(value, "outcome condition");
  const step = readString(condition, "step");
  switch (condition["kind"]) {
    case "check_passes":
    case "change_set_merged":
      return { kind: condition["kind"], step, part: readString(condition, "part") };
    case "choice_made":
      // `part`: the export holding the choice, where `step` names a sub-workflow use (ADR-0043 D7).
      return { kind: "choice_made", step, choice: readString(condition, "choice"), ...keptMembers(condition, { part: (part) => readString({ part }, "part") }) };
    default:
      throw new JsonContractError("Unknown outcome condition kind.");
  }
}

/** A sub-workflow use's parent wiring and the definitions it binds (ADR-0043 D2), each left out when unset. */
const SUB_WORKFLOW_KEPT_READERS = {
  input_bindings: (bindings: unknown) => decodeRecord(bindings, "input bindings", decodeBinding),
  definitions: (definitions: unknown) => decodeRecord(definitions, "definitions", decodeDefinitionBinding),
} satisfies { readonly [K in Exclude<keyof ApiSchema<"SubWorkflowUse">, "key" | "role" | "workflow">]-?: (value: unknown) => NonNullable<ApiSchema<"SubWorkflowUse">[K]> };

/**
 * A placement of a Workflow (P-13, ADR-0043 D1): a pinned step, a pinned sub-workflow or an
 * abstract step, told apart by `step`, `workflow` or `accepts`. Members left out of the file
 * stay left out, so saving writes back what was read (ADR-0032 D8).
 */
export function decodeWorkflowStep(value: unknown): ApiSchema<"WorkflowStep"> {
  const step = readObject(value, "Workflow step");
  const key = readString(step, "key");
  // `dmw-workflow/1` has no roles: all its steps are the pipeline.
  const role = step["role"] === undefined ? "main" : readOneOf(step, "role", ROLES);
  if (step["workflow"] !== undefined) {
    return { key, role, workflow: readString(step, "workflow"), ...keptMembers(step, SUB_WORKFLOW_KEPT_READERS) };
  }
  if (step["accepts"] !== undefined) {
    return {
      key,
      role,
      accepts: readStrings(step["accepts"], "accepted definitions"),
      required: readBoolean(step, "required"),
      start: readOneOf(step, "start", STARTS),
      input_bindings: decodeRecord(step["input_bindings"], "input bindings", decodeBinding),
      label: readString(step, "label"),
      description: readString(step, "description"),
    };
  }
  return {
    key,
    role,
    step: readString(step, "step"),
    start: readOneOf(step, "start", STARTS),
    input_bindings: decodeRecord(step["input_bindings"], "input bindings", decodeBinding),
  };
}

function decodeDefinitionBinding(value: unknown): ApiSchema<"DefinitionBinding"> {
  const binding = readObject(value, "definition binding");
  return {
    definition: readString(binding, "definition"),
    ...keptMembers(binding, { input_bindings: (bindings: unknown) => decodeRecord(bindings, "input bindings", decodeBinding) }),
  };
}

function decodeBinding(value: unknown): ApiSchema<"InputBinding"> {
  const binding = readObject(value, "binding");
  return { from_step: readString(binding, "from_step"), part: readString(binding, "part") };
}

const OVERRIDE_ROUTE_KINDS = enumMembers<ApiSchema<"OverrideRouteKind">>({ agent: true, api_key: true });

function decodeCapabilityOverrides(content: JsonObject): CapabilityOverridesContent {
  return {
    kind: "capability_overrides",
    name: readString(content, "name"),
    description: readString(content, "description"),
    entries: readArray(content["entries"], "entries", (item) => {
      const entry = readObject(item, "entry");
      const value = entry["value"];
      if (typeof value !== "boolean" && typeof value !== "number" && typeof value !== "string") {
        throw new JsonContractError('Expected "value" to be a boolean, a number or a string.');
      }
      return {
        route_kind: readOneOf(entry, "route_kind", OVERRIDE_ROUTE_KINDS),
        route_match: readString(entry, "route_match"),
        model_match: readString(entry, "model_match"),
        key: readString(entry, "key"),
        value,
        note: readString(entry, "note"),
      };
    }),
  };
}

export function decodeArtifactContent(content: JsonObject): ArtifactContent {
  return {
    kind: "artifact",
    name: readString(content, "name"),
    media_type: readOneOf(content, "media_type", MEDIA_TYPES),
    ...keptMembers(content, { ...PROCESS_MEMBERS, description: (description) => readString({ description }, "description") }),
    schema: readString(content, "schema"),
    derived_from: content["derived_from"] === null || content["derived_from"] === undefined ? [] : readStrings(content["derived_from"], "derived from"),
    content: content["content"],
  };
}

function decodeToolInterface(content: JsonObject): ToolInterface {
  return {
    name: readString(content, "name"),
    description: readString(content, "description"),
    inputs: decodeRecord(content["inputs"], "tool inputs", (value) => {
      const input = readObject(value, "tool input");
      return {
        accepts: readArray(input["accepts"], "accepts", (kind) => oneOf(kind, VALUE_KINDS, "value kind")),
        required: readBoolean(input, "required"),
        description: optionalText(input["description"]),
      };
    }),
    parameters: decodeRecord(content["parameters"], "tool parameters", decodeToolParameter),
    outputs: decodeRecord(content["outputs"], "tool outputs", (value) => {
      const output = readObject(value, "tool output");
      return { kind: readOneOf(output, "kind", VALUE_KINDS), description: optionalText(output["description"]) };
    }),
  };
}

function decodeSkillContent(content: JsonObject): SkillContent {
  return {
    kind: "skill",
    name: readString(content, "name"),
    description: readString(content, "description"),
    body: readString(content, "body"),
    files: readArray(content["files"], "bundled files", (value) => {
      const file = readObject(value, "bundled file");
      const path = readString(file, "path");
      return file["tool"] === undefined
        ? { path, artifact: decodeItemSelector(file["artifact"], "bundled artifact") }
        : { path, tool: readString(file, "tool") };
    }),
    ...keptMembers(content, PROCESS_MEMBERS),
  };
}

function optionalText(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}
